import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useUser } from '../auth/UserContext';
import { downloadReportForUser } from '../utils/scoreReport';
import * as api from '../api/client';

interface TeamData {
  id: string;
  name: string;
  territory: string;
  teamLeadId: string | null;
  teamLeadName: string | null;
  members: { id: string; fullName: string; role: string; appTerritory: string; monthlyTarget: number }[];
}

interface UserScore {
  userId: string;
  name: string;
  overall: number;
  factors: { key: string; label: string; ratio: number }[];
  totalIssues: number;
  error?: string;
}

interface TeamScoreData {
  scores: UserScore[];
  average: number;
  top: UserScore | null;
  bottom: UserScore | null;
}

function scoreColor(ratio: number): string {
  if (ratio >= 1.0) return 'var(--state-positive)';
  if (ratio >= 0.75) return 'var(--text-primary)';
  return 'var(--state-warning)';
}

function progressClass(ratio: number): string {
  if (ratio >= 1.0) return 'progress-fill-sage';
  if (ratio >= 0.75) return 'progress-fill-teal';
  return 'progress-fill-warning';
}

export function TeamLeadView() {
  const navigate = useNavigate();
  const { user } = useUser();
  const [searchParams] = useSearchParams();
  // Links from the team alert emails carry ?teamId=... so they open straight
  // onto that team rather than the full list.
  const focusTeamId = searchParams.get('teamId');
  const [teams, setTeams] = useState<TeamData[]>([]);
  const [teamScores, setTeamScores] = useState<Record<string, TeamScoreData>>({});
  const [loading, setLoading] = useState(true);
  const [loadingScores, setLoadingScores] = useState(false);
  const [error, setError] = useState('');
  const [downloadingId, setDownloadingId] = useState<string | null>(null);

  const handleDownload = async (userId: string) => {
    setDownloadingId(userId);
    try {
      await downloadReportForUser(userId);
    } catch {
      alert('Could not generate the report. Please try again.');
    }
    setDownloadingId(null);
  };

  const loadTeams = useCallback(async () => {
    setLoading(true);
    try {
      const result = await api.getSettingsTeams();
      let teamsData = (result.teams || []).filter((t: TeamData) => t.members.length > 0);

      // Team leads only see their own team; directors see their territory; admins see all
      if (user?.role === 'team_lead' && user.teamId) {
        teamsData = teamsData.filter((t: TeamData) => t.id === user.teamId);
      } else if (user?.role === 'director' && user.territory) {
        teamsData = teamsData.filter((t: TeamData) => t.territory === user.territory);
      }
      // admin sees all
      setTeams(teamsData);
      setError('');

      // Load scores for each team
      setLoadingScores(true);
      const scorePromises = teamsData.map(async (team: TeamData) => {
        try {
          const scores = await api.getTeamScores(team.id);
          return { teamId: team.id, data: scores };
        } catch {
          return { teamId: team.id, data: { scores: [], average: 0, top: null, bottom: null } };
        }
      });
      const results = await Promise.all(scorePromises);
      const scoresMap: Record<string, TeamScoreData> = {};
      for (const r of results) scoresMap[r.teamId] = r.data;
      setTeamScores(scoresMap);
      setLoadingScores(false);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadTeams(); }, [loadTeams]);

  if (loading) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '60vh' }}>
        <span style={{ color: 'var(--text-secondary)' }}>Loading teams...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ maxWidth: '480px', margin: '40px auto', textAlign: 'center' }}>
        <div className="warning-banner" style={{ marginBottom: '16px' }}>{error}</div>
        <button className="btn btn-primary" onClick={loadTeams}>Retry</button>
      </div>
    );
  }

  if (teams.length === 0) {
    return (
      <div>
        <h1 style={{ fontSize: '16px', fontWeight: 500, marginBottom: '24px' }}>Team Overview</h1>
        <div className="card" style={{ textAlign: 'center', padding: '40px' }}>
          <p style={{ color: 'var(--text-secondary)' }}>No teams with members yet. Set up teams in Settings first.</p>
        </div>
      </div>
    );
  }

  // If the URL targets a specific team (from an alert email), show just that
  // one — falling back to the full list if it isn't visible to this user.
  const focused = focusTeamId ? teams.filter(t => t.id === focusTeamId) : [];
  const visibleTeams = focused.length > 0 ? focused : teams;

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
        <h1 style={{ fontSize: '16px', fontWeight: 500 }}>Team Overview</h1>
        {focused.length > 0 && teams.length > 1 && (
          <button className="btn btn-secondary" onClick={() => navigate('/team')}>
            Show all teams
          </button>
        )}
      </div>

      {visibleTeams.map(team => {
        const scoreData = teamScores[team.id];
        const avg = scoreData?.average || 0;

        return (
          <div key={team.id} className="card" style={{ marginBottom: '16px' }}>
            {/* Team header */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
              <div>
                <span style={{ fontSize: '14px', fontWeight: 500 }}>{team.name}</span>
                <span style={{ marginLeft: '8px', fontSize: '12px', color: 'var(--text-secondary)' }}>{team.territory}</span>
                {team.teamLeadName && (
                  <span className="pill pill-teal" style={{ marginLeft: '8px' }}>Lead: {team.teamLeadName}</span>
                )}
              </div>
              <span className="pill pill-teal">{team.members.length} member{team.members.length !== 1 ? 's' : ''}</span>
            </div>

            {/* Summary cards */}
            {scoreData && (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '12px', marginBottom: '16px' }}>
                <div style={{ background: 'var(--bg-page)', borderRadius: '8px', padding: '12px' }}>
                  <div style={{ fontSize: '11px', color: 'var(--text-secondary)', marginBottom: '4px' }}>Team Average</div>
                  <div style={{ fontSize: '22px', fontWeight: 500, color: scoreColor(avg) }}>
                    {loadingScores ? '...' : `${Math.round(avg * 100)}%`}
                  </div>
                </div>
                {scoreData.top && (
                  <div style={{ background: 'var(--bg-page)', borderRadius: '8px', padding: '12px' }}>
                    <div style={{ fontSize: '11px', color: 'var(--text-secondary)', marginBottom: '4px' }}>Top Performer</div>
                    <div style={{ fontSize: '13px', fontWeight: 500 }}>{scoreData.top.name}</div>
                    <div style={{ fontSize: '12px', color: 'var(--state-positive)' }}>{Math.round(scoreData.top.overall * 100)}%</div>
                  </div>
                )}
                {scoreData.bottom && scoreData.bottom.userId !== scoreData.top?.userId && (
                  <div style={{ background: 'var(--bg-page)', borderRadius: '8px', padding: '12px' }}>
                    <div style={{ fontSize: '11px', color: 'var(--text-secondary)', marginBottom: '4px' }}>Needs Attention</div>
                    <div style={{ fontSize: '13px', fontWeight: 500 }}>{scoreData.bottom.name}</div>
                    <div style={{ fontSize: '12px', color: 'var(--state-warning)' }}>{Math.round(scoreData.bottom.overall * 100)}%</div>
                  </div>
                )}
              </div>
            )}

            {/* Members table with scores */}
            <table className="data-table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Score</th>
                  <th style={{ width: '120px' }}></th>
                  <th>Issues</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {team.members.map(m => {
                  const memberScore = scoreData?.scores.find(s => s.userId === m.id);
                  const overall = memberScore?.overall ?? -1;
                  return (
                    <tr key={m.id}>
                      <td style={{ fontWeight: 500 }}>
                        {m.fullName}
                        {m.id === team.teamLeadId && (
                          <span className="pill pill-teal" style={{ marginLeft: '6px', fontSize: '10px' }}>lead</span>
                        )}
                      </td>
                      <td style={{ fontWeight: 500, color: overall >= 0 ? scoreColor(overall) : 'var(--text-secondary)' }}>
                        {overall >= 0 ? `${Math.round(overall * 100)}%` : (loadingScores ? '...' : '–')}
                      </td>
                      <td>
                        {overall >= 0 && (
                          <div className="progress-track">
                            <div className={`progress-fill ${progressClass(overall)}`} style={{ width: `${Math.round(overall * 100)}%` }} />
                          </div>
                        )}
                      </td>
                      <td>
                        {memberScore && (
                          <span className={`pill ${memberScore.totalIssues === 0 ? 'pill-sage' : memberScore.totalIssues <= 5 ? 'pill-teal' : 'pill-warning'}`}>
                            {memberScore.totalIssues}
                          </span>
                        )}
                      </td>
                      <td>
                        <div style={{ display: 'flex', gap: '6px' }}>
                          <button
                            className="btn btn-primary"
                            style={{ padding: '4px 10px', fontSize: '11px' }}
                            onClick={() => navigate(`/?userId=${m.id}`)}
                          >
                            View
                          </button>
                          <button
                            className="btn btn-secondary"
                            style={{ padding: '4px 10px', fontSize: '11px' }}
                            disabled={downloadingId === m.id}
                            onClick={() => handleDownload(m.id)}
                            title="Download PDF report"
                          >
                            {downloadingId === m.id ? '...' : '⬇ PDF'}
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        );
      })}
    </div>
  );
}
